/**
 * Inbox.jsx — universal inbox for all roles.
 *
 * Two tabs:
 *   1) Messages  → 1:1 DM threads (admin↔user/writer, user↔admin)
 *   2) Announcements → admin broadcasts visible to the current viewer
 *
 * Composer can:
 *   - DM the platform admin (auto-resolved via /messaging/admin-id)
 *   - For admins, DM any user/writer via the searchable picker
 *
 * Endpoints used:
 *   GET /api/messaging/threads
 *   GET /api/messaging/threads/{key}     (also marks DM thread read)
 *   POST /api/messaging/dm
 *   GET /api/messaging/broadcasts
 *   POST /api/messaging/broadcasts/{id}/read
 *   POST /api/messaging/upload-image
 *   GET /api/messaging/admin-id
 *   GET /api/messaging/recipients?q=
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Send,
  Image as ImageIcon,
  Loader2,
  Bell,
  MessageSquare,
  ArrowLeft,
  Shield,
  PenTool,
  User as UserIcon,
  Megaphone,
} from 'lucide-react';
import { toast } from 'sonner';
import { authFetch, checkAuth } from '../utils/authUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString();
};

const RoleBadge = ({ role }) => {
  if (role === 'admin') {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-red-600/20 text-red-300 text-[10px] font-bold border border-red-500/30">
        <Shield className="w-2.5 h-2.5" /> ADMIN
      </span>
    );
  }
  if (role === 'writer') {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-purple-600/20 text-purple-300 text-[10px] font-bold border border-purple-500/30">
        <PenTool className="w-2.5 h-2.5" /> WRITER
      </span>
    );
  }
  return null;
};

const Avatar = ({ user, size = 'md' }) => {
  const cls = size === 'sm' ? 'w-8 h-8' : 'w-10 h-10';
  if (user?.picture) {
    return <img src={user.picture} alt={user?.name || ''} className={`${cls} rounded-full object-cover`} />;
  }
  return (
    <div className={`${cls} rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center`}>
      <UserIcon className="w-5 h-5 text-white" />
    </div>
  );
};

const Inbox = () => {
  const [searchParams] = useSearchParams();
  const dmTarget = searchParams.get('dm');
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState('messages'); // 'messages' | 'announcements'
  const [threads, setThreads] = useState([]);
  const [activeKey, setActiveKey] = useState(null);
  const [activeThread, setActiveThread] = useState(null);
  const [broadcasts, setBroadcasts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [draft, setDraft] = useState('');
  const [pendingImage, setPendingImage] = useState(null); // { file, url }
  const [defaultAdmin, setDefaultAdmin] = useState(null);
  const fileRef = useRef(null);

  // -- Bootstrap ---------------------------------------------------------
  useEffect(() => {
    (async () => {
      const u = await checkAuth();
      setMe(u);
    })();
  }, []);

  const fetchThreads = useCallback(async () => {
    try {
      const r = await authFetch(`${BACKEND_URL}/api/messaging/threads`);
      if (r.ok) {
        const d = await r.json();
        setThreads(d.threads || []);
      }
    } catch (_) { /* silent */ }
  }, []);

  const fetchBroadcasts = useCallback(async () => {
    try {
      const r = await authFetch(`${BACKEND_URL}/api/messaging/broadcasts`);
      if (r.ok) {
        const d = await r.json();
        setBroadcasts(d.broadcasts || []);
      }
    } catch (_) { /* silent */ }
  }, []);

  const fetchAdmin = useCallback(async () => {
    try {
      const r = await authFetch(`${BACKEND_URL}/api/messaging/admin-id`);
      if (r.ok) {
        const d = await r.json();
        setDefaultAdmin(d.admin || null);
      }
    } catch (_) { /* silent */ }
  }, []);

  useEffect(() => {
    if (!me) return;
    setLoading(true);
    Promise.all([fetchThreads(), fetchBroadcasts(), fetchAdmin()]).finally(() => setLoading(false));
  }, [me, fetchThreads, fetchBroadcasts, fetchAdmin]);

  // Deep-link: /inbox?dm=<user_id> — seed a thread with that recipient.
  // Used by the Admin Messages tab "Send DM" buttons so admins land in the
  // exact conversation they wanted to start. Fires once after the user
  // payload is available so we always have `me.user_id` for the thread key.
  useEffect(() => {
    if (!me || !dmTarget) return;
    const key = [me.user_id, dmTarget].sort().join('__');
    // If the thread already exists in our list, just open it; otherwise
    // seed a fresh one by fetching the recipient profile so the header
    // and avatar render correctly even pre-first-message.
    const existing = threads.find((t) => t.thread_key === key);
    if (existing) {
      openThread(key);
      return;
    }
    (async () => {
      try {
        const r = await authFetch(`${BACKEND_URL}/api/messaging/recipients?q=${encodeURIComponent(dmTarget)}`);
        const d = await r.json();
        const recipient = (d.recipients || []).find((u) => u.user_id === dmTarget) || { user_id: dmTarget, name: 'New conversation', role: 'user' };
        setActiveKey(key);
        setActiveThread({ thread_key: key, other: recipient, messages: [] });
      } catch (_) {
        setActiveKey(key);
        setActiveThread({ thread_key: key, other: { user_id: dmTarget, name: 'New conversation', role: 'user' }, messages: [] });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me, dmTarget, threads.length]);

  // -- Open a thread (also marks it read server-side) --------------------
  const openThread = async (key) => {
    setActiveKey(key);
    setActiveThread(null);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/messaging/threads/${encodeURIComponent(key)}`);
      if (!r.ok) {
        toast.error('Could not load conversation');
        return;
      }
      const d = await r.json();
      setActiveThread(d);
      // Locally zero the unread count (server already reset it).
      setThreads((prev) => prev.map((t) => (t.thread_key === key ? { ...t, unread: 0 } : t)));
    } catch (_) {
      toast.error('Could not load conversation');
    }
  };

  // -- Image upload ------------------------------------------------------
  const onPickImage = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    if (!f.type.startsWith('image/')) {
      toast.error('Please choose an image file');
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      toast.error('Image too large (max 5MB)');
      return;
    }
    const form = new FormData();
    form.append('image', f);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/messaging/upload-image`, {
        method: 'POST',
        body: form,
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || `Upload failed (${r.status})`);
      setPendingImage({ url: d.url });
    } catch (err) {
      toast.error(err.message || 'Image upload failed');
    }
  };

  // -- Send ------------------------------------------------------------
  const sendCurrent = async () => {
    if (!activeThread?.other?.user_id) return;
    const text = draft.trim();
    const image_url = pendingImage?.url || null;
    if (!text && !image_url) return;
    setSending(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/messaging/dm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          recipient_id: activeThread.other.user_id,
          body: text,
          image_url,
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || `Send failed (${r.status})`);
      setActiveThread((prev) => ({
        ...prev,
        messages: [...(prev?.messages || []), d.message],
      }));
      setDraft('');
      setPendingImage(null);
      fetchThreads();
    } catch (err) {
      toast.error(err.message || 'Could not send');
    } finally {
      setSending(false);
    }
  };

  // -- Compose new DM to admin ------------------------------------------
  const startAdminDM = async () => {
    if (!defaultAdmin?.user_id) {
      toast.error('No admin available to message');
      return;
    }
    const key = [me.user_id, defaultAdmin.user_id].sort().join('__');
    // Seed an in-memory thread so the right pane opens immediately even
    // when this is the first message.
    setActiveKey(key);
    setActiveThread({ thread_key: key, other: defaultAdmin, messages: [] });
  };

  // -- Mark broadcast read ----------------------------------------------
  const markBroadcastRead = async (id) => {
    setBroadcasts((prev) => prev.map((b) => (b.message_id === id ? { ...b, read: true } : b)));
    try {
      await authFetch(`${BACKEND_URL}/api/messaging/broadcasts/${id}/read`, { method: 'POST' });
    } catch (_) { /* silent */ }
  };

  const unreadBroadcasts = useMemo(
    () => broadcasts.filter((b) => !b.read).length,
    [broadcasts],
  );
  const unreadThreads = useMemo(
    () => threads.reduce((s, t) => s + (t.unread || 0), 0),
    [threads],
  );

  // -- Render ----------------------------------------------------------
  if (!me) {
    return (
      <main className="max-w-5xl mx-auto px-4 py-8" data-testid="inbox-loading">
        <p className="text-gray-400">Loading inbox…</p>
      </main>
    );
  }

  return (
    <main className="max-w-5xl mx-auto px-4 py-6" data-testid="inbox-page">
      <h1 className="text-2xl sm:text-3xl font-bold text-white mb-4 flex items-center gap-2">
        <MessageSquare className="w-6 h-6 text-indigo-400" /> Inbox
      </h1>

      {/* Tab strip */}
      <div className="flex items-center gap-2 mb-4 border-b border-slate-700">
        <button
          type="button"
          onClick={() => setTab('messages')}
          className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${
            tab === 'messages'
              ? 'border-indigo-500 text-white'
              : 'border-transparent text-slate-400 hover:text-white'
          }`}
          data-testid="inbox-tab-messages"
        >
          Messages
          {unreadThreads > 0 && (
            <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-pink-500 text-white text-[10px] font-bold">
              {unreadThreads}
            </span>
          )}
        </button>
        <button
          type="button"
          onClick={() => setTab('announcements')}
          className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${
            tab === 'announcements'
              ? 'border-indigo-500 text-white'
              : 'border-transparent text-slate-400 hover:text-white'
          }`}
          data-testid="inbox-tab-announcements"
        >
          Announcements
          {unreadBroadcasts > 0 && (
            <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-pink-500 text-white text-[10px] font-bold">
              {unreadBroadcasts}
            </span>
          )}
        </button>
      </div>

      {loading && (
        <div className="py-8 text-center text-slate-400">
          <Loader2 className="w-5 h-5 inline-block animate-spin mr-2" /> Loading…
        </div>
      )}

      {/* MESSAGES TAB */}
      {tab === 'messages' && !loading && (
        <div className="grid grid-cols-1 md:grid-cols-[300px_1fr] gap-4">
          {/* Thread list (hidden when a thread is open on mobile) */}
          <aside
            className={`${activeKey ? 'hidden md:block' : 'block'} rounded-xl bg-slate-800/50 border border-slate-700 max-h-[70vh] overflow-y-auto`}
            data-testid="inbox-thread-list"
          >
            {/* New DM to admin (only meaningful for non-admins) */}
            {me.role !== 'admin' && defaultAdmin && (
              <button
                type="button"
                onClick={startAdminDM}
                className="w-full p-3 text-left border-b border-slate-700 hover:bg-slate-700/40 transition-colors"
                data-testid="inbox-new-dm-admin"
              >
                <div className="flex items-center gap-2 text-sm text-indigo-300 font-semibold">
                  <Send className="w-4 h-4" /> Message the SmarterYou admin
                </div>
                <div className="text-xs text-slate-400 mt-0.5">
                  Got a question or feedback? Drop us a line.
                </div>
              </button>
            )}
            {threads.length === 0 ? (
              <div className="p-4 text-center text-sm text-slate-500">
                No conversations yet.
              </div>
            ) : (
              threads.map((t) => (
                <button
                  key={t.thread_key}
                  type="button"
                  onClick={() => openThread(t.thread_key)}
                  className={`w-full p-3 text-left border-b border-slate-700/50 hover:bg-slate-700/40 transition-colors ${
                    activeKey === t.thread_key ? 'bg-slate-700/60' : ''
                  } ${t.unread > 0 ? 'bg-pink-500/5' : ''}`}
                  data-testid={`inbox-thread-${t.thread_key}`}
                >
                  <div className="flex items-start gap-2.5">
                    <Avatar user={t.other} size="sm" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 text-sm font-semibold text-white truncate">
                        <span className="truncate">{t.other?.name || 'Unknown'}</span>
                        <RoleBadge role={t.other?.role} />
                      </div>
                      <div className="text-xs text-slate-400 truncate mt-0.5">
                        {t.last_message_preview || '—'}
                      </div>
                      <div className="text-[10px] text-slate-500 mt-0.5">
                        {formatTime(t.last_message_at)}
                      </div>
                    </div>
                    {t.unread > 0 && (
                      <span className="shrink-0 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-pink-500 text-white text-[10px] font-bold">
                        {t.unread}
                      </span>
                    )}
                  </div>
                </button>
              ))
            )}
          </aside>

          {/* Conversation pane */}
          <section
            className={`${activeKey ? 'block' : 'hidden md:block'} rounded-xl bg-slate-800/50 border border-slate-700 flex flex-col min-h-[60vh] max-h-[75vh]`}
            data-testid="inbox-conversation"
          >
            {!activeKey ? (
              <div className="flex-1 flex items-center justify-center p-6 text-center text-sm text-slate-400">
                Pick a conversation on the left, or start a new one.
              </div>
            ) : (
              <>
                <header className="flex items-center gap-2 px-3 py-2.5 border-b border-slate-700">
                  <button
                    type="button"
                    onClick={() => { setActiveKey(null); setActiveThread(null); }}
                    className="md:hidden p-1 -ml-1 rounded hover:bg-slate-700"
                    aria-label="Back"
                  >
                    <ArrowLeft className="w-4 h-4 text-slate-300" />
                  </button>
                  <Avatar user={activeThread?.other} size="sm" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 text-sm font-semibold text-white truncate">
                      <span className="truncate">{activeThread?.other?.name || 'Loading…'}</span>
                      <RoleBadge role={activeThread?.other?.role} />
                    </div>
                    <div className="text-[11px] text-slate-500 truncate">
                      {activeThread?.other?.email}
                    </div>
                  </div>
                </header>

                <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2" data-testid="inbox-messages-list">
                  {(activeThread?.messages || []).map((m) => {
                    const mine = m.sender_id === me.user_id;
                    return (
                      <div
                        key={m.message_id}
                        className={`flex ${mine ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${
                            mine
                              ? 'bg-indigo-600 text-white rounded-br-sm'
                              : 'bg-slate-700 text-slate-100 rounded-bl-sm'
                          }`}
                          data-testid={`message-${m.message_id}`}
                        >
                          {m.image_url && (
                            <img
                              src={m.image_url}
                              alt="attachment"
                              className="rounded-lg mb-1 max-h-64 max-w-full object-cover"
                            />
                          )}
                          {m.body && (
                            <div className="whitespace-pre-wrap break-words">{m.body}</div>
                          )}
                          <div className={`text-[10px] mt-1 ${mine ? 'text-indigo-200/80' : 'text-slate-400'}`}>
                            {formatTime(m.created_at)}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {(!activeThread?.messages || activeThread.messages.length === 0) && (
                    <div className="text-center text-xs text-slate-500 py-6">
                      Say hi — no messages yet.
                    </div>
                  )}
                </div>

                {/* Composer */}
                <div className="border-t border-slate-700 p-2">
                  {pendingImage && (
                    <div className="mb-2 flex items-center gap-2 text-xs text-slate-300">
                      <img src={pendingImage.url} alt="" className="w-12 h-12 rounded object-cover" />
                      <span>Image attached</span>
                      <button
                        type="button"
                        onClick={() => setPendingImage(null)}
                        className="ml-auto text-rose-400 hover:underline"
                      >
                        Remove
                      </button>
                    </div>
                  )}
                  <div className="flex items-end gap-2">
                    <textarea
                      value={draft}
                      onChange={(e) => setDraft(e.target.value.slice(0, 4000))}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          sendCurrent();
                        }
                      }}
                      rows={2}
                      placeholder="Write a message…"
                      className="flex-1 rounded-lg bg-slate-900 border border-slate-700 text-white text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 resize-none"
                      data-testid="inbox-composer-input"
                    />
                    <input
                      ref={fileRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={onPickImage}
                      data-testid="inbox-composer-image-input"
                    />
                    <button
                      type="button"
                      onClick={() => fileRef.current?.click()}
                      className="p-2.5 rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-200"
                      aria-label="Attach image"
                      data-testid="inbox-composer-image-btn"
                    >
                      <ImageIcon className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={sendCurrent}
                      disabled={sending || (!draft.trim() && !pendingImage)}
                      className="p-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-600 disabled:cursor-not-allowed text-white"
                      aria-label="Send"
                      data-testid="inbox-composer-send-btn"
                    >
                      {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              </>
            )}
          </section>
        </div>
      )}

      {/* ANNOUNCEMENTS TAB */}
      {tab === 'announcements' && !loading && (
        <div className="space-y-3" data-testid="inbox-announcements">
          {broadcasts.length === 0 ? (
            <div className="rounded-xl bg-slate-800/50 border border-slate-700 p-8 text-center text-sm text-slate-400">
              No announcements yet. When the admin team posts updates, they'll show up here.
            </div>
          ) : (
            broadcasts.map((b) => (
              <article
                key={b.message_id}
                onClick={() => !b.read && markBroadcastRead(b.message_id)}
                className={`rounded-xl border p-4 transition-colors cursor-pointer ${
                  b.read
                    ? 'bg-slate-800/40 border-slate-700'
                    : 'bg-pink-500/5 border-pink-500/30'
                }`}
                data-testid={`broadcast-${b.message_id}`}
              >
                <div className="flex items-center gap-2 mb-1.5">
                  <Megaphone className="w-4 h-4 text-pink-400" />
                  <span className="text-xs uppercase tracking-wider font-bold text-pink-300">
                    Announcement
                  </span>
                  <span className="text-[10px] uppercase tracking-wider font-bold text-slate-400 bg-slate-700/60 px-1.5 py-0.5 rounded">
                    {b.broadcast_audience}
                  </span>
                  <span className="ml-auto text-[10px] text-slate-500">
                    {formatTime(b.created_at)}
                  </span>
                </div>
                {b.image_url && (
                  <img
                    src={b.image_url}
                    alt="announcement"
                    className="rounded-lg mb-2 max-h-72 w-full object-cover"
                  />
                )}
                <div className="text-sm text-slate-100 whitespace-pre-wrap break-words">
                  {b.body}
                </div>
              </article>
            ))
          )}
        </div>
      )}
    </main>
  );
};

export default Inbox;
